/**
 * Card View for Search Programs matching Crizac production course card layout.
 *
 * Each card displays:
 * - Campus banner with overlapping university avatar and selection checkbox
 * - Program name and University
 * - Location (City/Country) and Study Level
 * - Key metrics: Tuition Fee, Application Fee, Duration, Intakes
 * - Badges (Scholarship, Free to Apply)
 * - Clear action buttons: [ VIEW DETAILS ] and [ APPLY NOW ]
 */
import { useState } from 'react';
import { assetUrl } from '../assetUrl.js';

function initials(name) {
  return (name ?? '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

/**
 * The course's university logo in the avatar badge, falling back to an initials
 * monogram when the university has no image or the URL 404s. The image is the
 * university's, mapped from `university_id` via the options `universityAssets`
 * map — the course search index carries no image of its own.
 */
function UniAvatar({ src, name }) {
  const [broken, setBroken] = useState(false);
  if (src && !broken) {
    return (
      <img
        className="prog-uni-avatar prog-uni-avatar-img"
        src={src}
        alt=""
        loading="lazy"
        title={name}
        onError={() => setBroken(true)}
      />
    );
  }
  return <div className="prog-uni-avatar" title={name}>{initials(name)}</div>;
}

function MoneyText({ hit }) {
  if (!hit.fee_known) return <span className="muted">On request</span>;
  const base = `£${hit.tuition_base.toLocaleString()}`;
  const showsOriginal = hit.tuition_currency
    && hit.tuition_currency !== 'GBP'
    && hit.tuition_original;

  if (!showsOriginal) return base;
  return (
    <>
      {base}
      <span className="muted" style={{ fontSize: 11, marginLeft: 4 }}>
        ({hit.tuition_currency} {Math.round(hit.tuition_original).toLocaleString()})
      </span>
    </>
  );
}

function DurationText({ hit }) {
  if (!hit.duration_known) return <span className="muted">—</span>;
  const min = hit.duration_min_months ?? hit.duration_months;
  const max = hit.duration_max_months ?? hit.duration_months;
  if (min === max) {
    if (min >= 12 && min % 12 === 0) {
      const yrs = min / 12;
      return `${yrs} Year${yrs > 1 ? 's' : ''}`;
    }
    return `${min} Months`;
  }
  return `${min}–${max} Months`;
}

export function ProgramCardGrid({ hits, found, onOpen, onApply, universityAssets }) {
  if (found === 0) {
    return (
      <div className="empty">
        <b>No programs match all of these filters</b>
        Remove one of the tags above to widen the search.
      </div>
    );
  }

  const handleApply = (e, h) => {
    e.stopPropagation();
    if (onApply) {
      onApply(h);
    } else {
      window.alert(`Application started for ${h.name} at ${h.university_name}`);
    }
  };

  const handleOpen = (e, h) => {
    e.stopPropagation();
    onOpen?.(h.id);
  };

  return (
    <div className="program-cards-grid" id="programCardsGrid">
      {hits.map((h) => {
        const assets = universityAssets?.[h.university_id];
        const intakes = (h.intakes ?? []).slice(0, 2).join(', ');

        return (
          <div
            className="program-card"
            key={h.id}
            role="article"
            aria-label={`${h.name} at ${h.university_name}`}
          >
            {/* Top Banner: the university's banner photo (gradient fallback) with
                its logo badge and the selection checkbox. */}
            <div className="prog-banner">
              <div className="prog-banner-img" />
              {assets?.banner && (
                <img
                  className="prog-banner-photo"
                  src={assetUrl(assets.banner)}
                  alt=""
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              )}
              <div className="prog-card-select">
                <input
                  type="checkbox"
                  aria-label={`Select ${h.name}`}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <UniAvatar src={assetUrl(assets?.logo)} name={h.university_name} />
            </div>

            {/* Content Area */}
            <div className="prog-card-body">
              <h3 className="prog-card-title" title={h.name}>
                {h.name}
              </h3>
              <div className="prog-card-uni" title={h.university_name}>
                {h.university_name}
              </div>

              <div className="prog-card-subrow">
                <span className="prog-card-loc">
                  {h.city || h.country_name || 'Campus'}
                </span>
                {h.degree_group && (
                  <span className="prog-card-level">{h.degree_group}</span>
                )}
              </div>

              {/* Key Specs */}
              <div className="prog-card-specs">
                <div className="prog-spec-row">
                  <span className="spec-k">Tuition Fee:</span>
                  <span className="spec-v"><MoneyText hit={h} /></span>
                </div>
                <div className="prog-spec-row">
                  <span className="spec-k">Application Fee:</span>
                  <span className="spec-v">
                    {h.application_fee ? `£${h.application_fee}` : '£0.00'}
                  </span>
                </div>
                <div className="prog-spec-row">
                  <span className="spec-k">Duration:</span>
                  <span className="spec-v"><DurationText hit={h} /></span>
                </div>
                {intakes && (
                  <div className="prog-spec-row">
                    <span className="spec-k">Intake:</span>
                    <span className="spec-v spec-intake">{intakes}</span>
                  </div>
                )}
              </div>

              {/* Badges */}
              <div className="prog-card-badges">
                {h.scholarship && (
                  <span className="badge badge-scholarship">Scholarship</span>
                )}
                {h.free_to_apply && (
                  <span className="badge badge-free">Free to apply</span>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="prog-card-actions">
              <button
                type="button"
                className="btn-card-details"
                onClick={(e) => handleOpen(e, h)}
              >
                VIEW DETAILS
              </button>
              <button
                type="button"
                className="btn-card-apply"
                onClick={(e) => handleApply(e, h)}
              >
                APPLY NOW
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
